import { useEffect, useState } from 'react';
import { Dimensions, Platform } from 'react-native';
import { tokens } from '../theme/tokens';

export type Breakpoint = 'mobile' | 'tablet' | 'desktop' | 'wide';

interface ResponsiveConfig<T> {
  mobile?: T;
  tablet?: T;
  desktop?: T;
  wide?: T;
}

interface ResponsiveHook {
  width: number;
  height: number;
  breakpoint: Breakpoint;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isWide: boolean;
  orientation: 'portrait' | 'landscape';
  platform: 'ios' | 'android' | 'web' | 'windows' | 'macos';
}

const getBreakpoint = (width: number): Breakpoint => {
  if (width >= tokens.breakpoints.wide) return 'wide';
  if (width >= tokens.breakpoints.desktop) return 'desktop';
  if (width >= tokens.breakpoints.tablet) return 'tablet';
  return 'mobile';
};

export const useResponsive = (): ResponsiveHook => {
  const [dimensions, setDimensions] = useState(() => {
    const { width, height } = Dimensions.get('window');
    return { width, height };
  });

  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setDimensions({ width: window.width, height: window.height });
    });

    return () => subscription?.remove();
  }, []);

  const breakpoint = getBreakpoint(dimensions.width);
  const orientation = dimensions.width > dimensions.height ? 'landscape' : 'portrait';

  return {
    width: dimensions.width,
    height: dimensions.height,
    breakpoint,
    isMobile: breakpoint === 'mobile',
    isTablet: breakpoint === 'tablet',
    isDesktop: breakpoint === 'desktop' || breakpoint === 'wide',
    isWide: breakpoint === 'wide',
    orientation,
    platform: Platform.OS,
  };
};

export const useResponsiveValue = <T>(config: ResponsiveConfig<T>): T | undefined => {
  const { breakpoint } = useResponsive();

  const getValue = (): T | undefined => {
    switch (breakpoint) {
      case 'wide':
        return config.wide ?? config.desktop ?? config.tablet ?? config.mobile;
      case 'desktop':
        return config.desktop ?? config.tablet ?? config.mobile;
      case 'tablet':
        return config.tablet ?? config.mobile;
      case 'mobile':
        return config.mobile;
      default:
        return config.mobile;
    }
  };

  return getValue();
};

export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    if (Platform.OS !== 'web') {
      const { width } = Dimensions.get('window');

      if (query.includes('min-width')) {
        const minWidth = Number.parseInt(/\d+/.exec(query)?.[0] ?? '0', 10);
        setMatches(width >= minWidth);
      } else if (query.includes('max-width')) {
        const maxWidth = Number.parseInt(/\d+/.exec(query)?.[0] ?? '0', 10);
        setMatches(width <= maxWidth);
      }

      const subscription = Dimensions.addEventListener('change', ({ window }) => {
        if (query.includes('min-width')) {
          const minWidth = Number.parseInt(/\d+/.exec(query)?.[0] ?? '0', 10);
          setMatches(window.width >= minWidth);
        } else if (query.includes('max-width')) {
          const maxWidth = Number.parseInt(/\d+/.exec(query)?.[0] ?? '0', 10);
          setMatches(window.width <= maxWidth);
        }
      });

      return () => subscription?.remove();
    }

    // Web-specific implementation
    if (globalThis.window !== undefined && globalThis.matchMedia) {
      const mediaQuery = globalThis.matchMedia(query);
      setMatches(mediaQuery.matches);

      const handleChange = (e: MediaQueryListEvent) => setMatches(e.matches);
      mediaQuery.addEventListener('change', handleChange);

      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [query]);

  return matches;
};
